import React, { useEffect, useState } from "react"
import { DatePicker } from "@stardust-ds/react"
import dayjs from "dayjs"
import styles from "./index.module.scss"
import Label from "../Label"
import Button from "../Button"
import MoveEllipsisIcon from "../../../assets/icons/MoveEllipsisIcon"
import RcmpTimePicker from "../TimePicker"
import moment from "moment"
import { fnIsValidDateBr } from "../../../utils/date"

const oDatePickerStyle = {
  dayContainerStyle: {
    selected: {
      backgroundColor: "#6CB6E3",
      borderColor: "transparent",
      borderRadius: 4,
    },
    today: {
      color: "#6CB6E3",
      borderRadius: 0,
      outlineColor: "transparent",
    },
  },
  dayTextStyle: {
    today: {
      color: "#6CB6E3",
    },
  },
}

function RcmpDatePickerFooter(props) {
  const { fnOnCancel } = props

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Tab") {
        fnOnClose()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [fnOnClose])

  function fnOnClose() {
    if (document?.body?.lastChild) document.body.lastChild?.click()
  }

  function fnHandleCancel() {
    fnOnClose()
    fnOnCancel()
  }

  return (
    <div className={styles.datePickerFooter}>
      <Button variant="outline" liquid={true} onClick={fnHandleCancel}>
        CANCELAR
      </Button>
      <Button liquid={true} onClick={fnOnClose}>
        SELECIONAR
      </Button>
    </div>
  )
}

const RcmpDatePicker = React.forwardRef((props, ref) => {
  const {
    label,
    defaultValue,
    timePlaceholder,
    value,
    bIsDraggable,
    disabled,
    bIsDisabled,
    bTime = true,
    cTimeLabel,
    onChange,
    inputStartProps,
    ...oRest
  } = props

  function fnGetDate(
    value,
    cFormat = "YYYY-MM-DD - HH:mm",
    cFormatReturn = "YYYY-MM-DD",
  ) {
    try {
      if (value) {
        return moment(value, cFormat).format(cFormatReturn)
      }
      return null
    } catch (e) {
      return null
    }
  }

  function fnGetTime(value, cFormat = "YYYY-MM-DD - HH:mm") {
    try {
      if (value) {
        if (moment(value, cFormat).format("HH:mm") == "Invalid date") {
          return null
        }
        return moment(value, cFormat).format("HH:mm")
      }
      return null
    } catch (e) {
      return null
    }
  }

  const [dOldValue, fnSetDoldValue] = useState(
    fnGetDate(defaultValue, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]", "YYYY-MM-DD") ||
    fnGetDate(value, "DD/MM/YYYY - HH:mm", "YYYY-MM-DD"),
  )
  const [dOldTimeValue, fnSetDoldTimeValue] = useState(
    fnGetTime(defaultValue, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]") || fnGetTime(value),
  )
  const [dcurrentValue, fnSetDCurrentValue] = useState(
    (fnGetDate(defaultValue, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]", "YYYY-MM-DD") ||
      fnGetDate(value, "DD/MM/YYYY - HH:mm", "YYYY-MM-DD")) +
    " - " +
    fnGetTime(defaultValue, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]") || fnGetTime(value),
  )

  useEffect(() => {
    const dOldDate =
      fnGetDate(defaultValue, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]", "YYYY-MM-DD") ||
      fnGetDate(value, "DD/MM/YYYY - HH:mm", "YYYY-MM-DD")
    const dOldTime =
      fnGetTime(defaultValue, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]") || fnGetTime(value)

    const dcurrentValue = dOldDate + " - " + dOldTime
    fnSetDoldValue(dOldDate ?? "")
    fnSetDoldTimeValue(dOldTime ?? "")
    fnSetDCurrentValue(dcurrentValue ?? "")
  }, [defaultValue, value])

  function fnOnCancel() {
    fnSetDoldValue(fnGetDate(defaultValue) || fnGetDate(value))
    fnSetDoldTimeValue(fnGetTime(defaultValue) || fnGetTime(value))
  }

  function fnOnChangeDate(e) {
    try {
      // check if e is event
      if (!e[0]) {
        return
      }

      let time = fnGetTime(dcurrentValue)
      if (!fnIsValidDateBr(e?.length > 0 ? e[0] : defaultValue) === false) return

      const date = moment(e?.length > 0 ? e[0] : defaultValue).format("YYYY-MM-DD")

      if (!time || time === "Invalid date") {
        time = timePlaceholder || "12:00"
      }

      const newDate = date + " " + time

      fnSetDCurrentValue(newDate)
      onChange(newDate)
    } catch (e) {
      return
    }
  }

  function fnOnChangeTime(e) {
    try {
      let date = fnGetDate(dcurrentValue, "YYYY-MM-DD")

      // check if e is string or object
      let time
      if (typeof e === "string") {
        time = e
      } else {
        time = e.hour() + ":" + e.minute()
      }

      if (!date || date === "Invalid date") {
        date = moment().format("YYYY-MM-DD")
      }
      const newDate = date + " " + time

      fnSetDCurrentValue(newDate)
      onChange(newDate)
    } catch (e) {
      console.log(e)
    }
  }
  function handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }
  return (
    <div className={styles.container}>
      {bIsDraggable ? (
        <div className={styles.draggableComponent}>
          <MoveEllipsisIcon fill={"#E9EBEE"} />
          <Label className={styles.label}>{label}</Label>
        </div>
      ) : (
        <Label className={styles.label}>{label}</Label>
      )}
      <div className={styles.datePickerContainer}>
        <DatePicker
          ref={ref}
          style={oDatePickerStyle}
          footer={<RcmpDatePickerFooter fnOnCancel={fnOnCancel} />}
          onChangeCapture={(e) => fnOnChangeDate(e)}
          onChange={(e) => fnOnChangeDate(e)}
          onKeyDown={handleKeyDown}
          inputStartProps={{
            disabled: disabled || oRest?.inputStartProps?.disabled,
          }}
          defaultValue={{ start: dOldValue }}
          initialDate={dOldValue}
          autoComplete="off"
          {...oRest}
        />
        {bTime && (
          <RcmpTimePicker
            autoComplete="off"
            disabled={disabled || oRest?.inputStartProps?.disabled}
            cLabel={cTimeLabel}
            isFullWidth={true}
            defaultValue={dOldTimeValue}
            onChange={(e) => fnOnChangeTime(e)}
            {...oRest}
          />
        )}
      </div>
    </div>
  )
})

RcmpDatePicker.displayName = "RcmpDatePicker"

export default RcmpDatePicker
